import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

/**
 * Single MCQ question card for the pedagogical agent.
 * Used directly by FeedbackSection when rendering unified feedback items.
 */
const QuestionCard = ({ question, onSubmitAnswer }) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null); // { is_correct, correct_answer, explanation }
  const [submitting, setSubmitting] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);

  // Support both question_type (unified format) and type (legacy)
  const questionType = question.question_type || question.type || 'single';

  const handleOptionToggle = (optionKey) => {
    if (submitted) return;
    if (questionType === 'single') {
      setSelectedOptions([optionKey]);
    } else {
      setSelectedOptions(prev =>
        prev.includes(optionKey)
          ? prev.filter(k => k !== optionKey)
          : [...prev, optionKey]
      );
    }
  };

  const handleSubmit = async () => {
    if (selectedOptions.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      const res = await onSubmitAnswer(question.question_id, selectedOptions, {
        rubric: question.rubric,
        explanation: question.explanation,
        question: question.question,
        lo: question.lo
      });
      setResult(res);
      setSubmitted(true);
      setAttemptCount(prev => prev + 1);
    } catch (err) {
      console.error('Failed to submit answer:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTryAgain = () => {
    setSubmitted(false);
    setSelectedOptions([]);
    setResult(null);
  };

  const isCorrectOption = (key) => result?.correct_answer?.includes(key);
  const isSelectedOption = (key) => selectedOptions.includes(key);

  return (
    <div className="border-2 border-blue-300 rounded-lg overflow-hidden bg-blue-50/30 mb-4">
      {/* Header */}
      <div className="bg-blue-100 border-b border-blue-300 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-semibold text-blue-900">
            Question {question.question_id}
          </span>
          <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded">
            {questionType === 'single' ? 'Single Choice' : 'Multiple Choice'}
          </span>
        </div>
        {question.lo && (
          <span className="text-xs text-blue-700 italic">
            LO: {question.lo}
          </span>
        )}
      </div>

      {/* Question Body */}
      <div className="p-4">
        <div className="mb-4 text-gray-900 font-medium">
          <ReactMarkdown
            remarkPlugins={[remarkMath]}
            rehypePlugins={[rehypeKatex]}
          >
            {question.question}
          </ReactMarkdown>
        </div>

        {/* Options */}
        <div className="space-y-2">
          {question.options.map((option) => {
            const isSelected = isSelectedOption(option.key);
            // Only highlight correct when the answer IS correct; never reveal correct on wrong submission
            const showCorrect = submitted && result && result.is_correct && isCorrectOption(option.key);
            const showIncorrect = submitted && result && isSelected && !isCorrectOption(option.key);

            return (
              <button
                key={option.key}
                onClick={() => handleOptionToggle(option.key)}
                disabled={submitted}
                className={`w-full text-left p-3 border-2 rounded-lg transition-all duration-200 ${
                  showCorrect
                    ? 'border-green-500 bg-green-50'
                    : showIncorrect
                    ? 'border-red-500 bg-red-50'
                    : isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50/50'
                } ${submitted ? 'cursor-default' : 'cursor-pointer'}`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center ${
                    showCorrect
                      ? 'border-green-500 bg-green-500'
                      : showIncorrect
                      ? 'border-red-500 bg-red-500'
                      : isSelected
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-400'
                  }`}>
                    {showCorrect && (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                    {showIncorrect && (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    )}
                    {isSelected && !submitted && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <span className={`font-semibold mr-2 ${
                      showCorrect ? 'text-green-900' : showIncorrect ? 'text-red-900' : 'text-gray-700'
                    }`}>
                      {option.key}:
                    </span>
                    <span className={
                      showCorrect ? 'text-green-800' : showIncorrect ? 'text-red-800' : 'text-gray-800'
                    }>
                      {option.text}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Submit Button */}
        {!submitted && (
          <button
            onClick={handleSubmit}
            disabled={selectedOptions.length === 0 || submitting}
            className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors"
          >
            {submitting ? 'Checking...' : 'Check Answer'}
          </button>
        )}

        {/* Feedback */}
        {submitted && result && (
          <div className={`mt-4 p-4 rounded-lg ${
            result.is_correct
              ? 'bg-green-100 border border-green-300'
              : 'bg-red-100 border border-red-300'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                {result.is_correct ? (
                  <>
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold text-green-900">Correct!</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold text-red-900">Incorrect</span>
                  </>
                )}
              </div>
              {!result.is_correct && (
                <button
                  onClick={handleTryAgain}
                  className="text-xs font-medium px-3 py-1 bg-white border border-red-300 text-red-700 rounded hover:bg-red-50 transition-colors"
                >
                  Try Again {attemptCount > 0 && `(Attempt ${attemptCount})`}
                </button>
              )}
            </div>
            {result.explanation && (
              <div className={`text-sm mt-2 ${result.is_correct ? 'text-green-800' : 'text-red-800'}`}>
                <ReactMarkdown
                  remarkPlugins={[remarkMath]}
                  rehypePlugins={[rehypeKatex]}
                >
                  {result.explanation}
                </ReactMarkdown>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { QuestionCard };
export default QuestionCard;
